/**
 * Transient VFX driven by `vm.events` (DESIGN 6.4 and the WP6 brief): nova
 * rings, quake cracks, explosion ring + flash, beam-cross vignette, melee
 * wedges, wind, self-cast collapsing rings with an element glyph pop,
 * hazard placement flashes, thunder bolts, merge sparkles, magick rings,
 * revive rings, self-shock flashes, hit flashes on bodies, plus the
 * particle bursts, floating numbers and camera shakes those events imply.
 *
 * Effects live in a fixed pool; events allocate nothing beyond the number
 * strings. `onEvents` handles one frame's events in two passes so an `ff`
 * event can mark the `hit` number it belongs to regardless of order.
 */

import { MAGICKS, NOVA, QUAKE, type Element, type GameEvent, type GrandId, type Vec2 } from '@shared/index.js';
import type { ViewEnemy, ViewModel } from '../state/viewModel.js';
import { elementColor, ELEMENT_SWATCH, ENEMY_COLORS, playerColor, UI } from '../palette.js';
import { STAFF_TIP_H, UNIT, type Ctx } from './gfx.js';
import { damageElement, frozenSize, fxElement, FxShots, ICE_FILL_ALPHA, wallHpFrac, wallSet, type HazardSeen } from './spellArt.js';
import { hazardSegments } from './spells.js';
import { drawEffectsAir, drawEffectsGround, drawEffectsScreen } from './effectsDraw.js';
import type { Camera } from './camera.js';
import { FEEL } from './feel.js';
import { art } from './sprites.js';
import { enemySetName } from './spriteSets.js';
import { PC, type Particles } from './particles.js';
import type { Numbers } from './numbers.js';
import { ENEMY_VISUAL } from './enemies.js';
import { dominantOf } from './entities.js';

/** The colours a Grand Magick bursts in: its pieces'. */
const GRAND_COLOURS: Readonly<Record<GrandId, readonly [Element, Element]>> = {
  timestop: ['moment', 'time'], quickening: ['flux', 'time'], rewind: ['pulse', 'time'], greatflood: ['deluge', 'tide'],
  supernova: ['sun', 'plasma'], eternalwinter: ['winter', 'stillness'], doom: ['death', 'time'],
};

export interface EffectDeps { particles: Particles; numbers: Numbers; camera: Camera }

export type EffectKind = 'nova' | 'quake' | 'explosion' | 'wedge' | 'wind' | 'selfRing' | 'place' | 'bolt' | 'merge' | 'magick' | 'revive' | 'shock' | 'glyph' | 'muzzle' | 'vignette' | 'deathRing';

export interface Effect {
  active: boolean;
  kind: EffectKind;
  x: number;
  y: number;
  z: number;
  t: number;
  dur: number;
  r: number;
  ang: number;
  el: Element | null;
  el2: Element | null;
  color: string;
  n: number;
}

const POOL = 128;
/** Seconds between impact sprites on one target. */
const HIT_SPRITE_GAP_S = 0.12;

interface Flash { until: number; dur: number; peak: number }
interface Nudge { dx: number; dy: number; until: number }
/** An enemy that just died, drawn where it stood for `dur` seconds: its set's death clip when there is one, else a squash and fade. */
export interface Dying { e: ViewEnemy; t: number; dur: number }

export interface EntityPos extends Vec2 { z: number }

/** World position (and body mid-height) of any entity id in the view model; false when absent. */
export function findEntity(vm: ViewModel, id: number, out: EntityPos): boolean {
  for (const p of vm.players) if (p.id === id) { out.x = p.rx; out.y = p.ry; out.z = STAFF_TIP_H; return true; }
  for (const e of vm.enemies) if (e.id === id) { out.x = e.rx; out.y = e.ry; out.z = ENEMY_VISUAL[e.k].h * 0.5; return true; }
  for (const c of vm.corpses) if (c.id === id) { out.x = c.rx; out.y = c.ry; out.z = 0.2; return true; }
  return false;
}

function isFrozen(vm: ViewModel, id: number): boolean {
  for (const p of vm.players) if (p.id === id) return p.st.some((s) => s.id === 'frozen');
  for (const e of vm.enemies) if (e.id === id) return e.st.some((s) => s.id === 'frozen');
  return false;
}

export class Effects {
  private readonly pool: Effect[] = [];
  private readonly flashes = new Map<number, Flash>();
  private readonly nudges = new Map<number, Nudge>();
  private readonly ffTargets = new Set<number>();
  /** Enemies in their death squash; the renderer draws them, `step` retires them. */
  readonly dying: Dying[] = [];
  private stopWanted = 0;
  private readonly tmp: EntityPos = { x: 0, y: 0, z: 0 };
  private time = 0;
  /** One-shot sprites from the effects library: impacts, blasts, nova fronts, walls breaking. */
  readonly shots = new FxShots();
  /** When each target last showed an impact sprite (beams and sprays hit many times a second). */
  private readonly hitShown = new Map<number, number>();

  constructor() {
    for (let i = 0; i < POOL; i++) {
      this.pool.push({ active: false, kind: 'nova', x: 0, y: 0, z: 0, t: 0, dur: 1, r: 1, ang: 0, el: null, el2: null, color: UI.white, n: 0 });
    }
  }

  clear(): void {
    for (const e of this.pool) e.active = false;
    this.shots.clear();
    this.hitShown.clear();
    this.flashes.clear();
    this.nudges.clear();
    this.dying.length = 0;
    this.stopWanted = 0;
  }

  private spawn(kind: EffectKind, x: number, y: number, dur: number, r: number, color: string, el: Element | null = null, el2: Element | null = null, n = 0, ang = 0, z = 0): void {
    let slot: Effect | null = null;
    for (const e of this.pool) if (!e.active) { slot = e; break; }
    if (!slot) {
      slot = this.pool[0]!;
      for (const e of this.pool) if (e.t / e.dur > slot.t / slot.dur) slot = e;
    }
    slot.active = true; slot.kind = kind; slot.x = x; slot.y = y; slot.z = z; slot.t = 0; slot.dur = dur;
    slot.r = r; slot.ang = ang; slot.el = el; slot.el2 = el2; slot.color = color; slot.n = n;
  }

  /** 0..1 hit-flash strength for an entity id this frame (brighter and longer after a crit). */
  flashOf(id: number): number {
    const f = this.flashes.get(id);
    if (f === undefined) return 0;
    const left = f.until - this.time;
    if (left <= 0) { this.flashes.delete(id); return 0; }
    return Math.min(1, left / f.dur) * f.peak;
  }

  /** Screen-px shove for an entity id this frame: a hit pushes the body away from where it landed. */
  nudgeOf(id: number, out: Vec2): void {
    out.x = 0;
    out.y = 0;
    const n = this.nudges.get(id);
    if (n === undefined) return;
    const left = n.until - this.time;
    if (left <= 0) { this.nudges.delete(id); return; }
    const f = left / FEEL.hit.nudgeS;
    out.x = n.dx * f;
    out.y = n.dy * f;
  }

  /** Drop flashes and nudges that have run out; a body that died or left the view never asks for its own again. */
  sweep(): void {
    this.flashes.forEach((f, id) => { if (f.until <= this.time) this.flashes.delete(id); });
    this.nudges.forEach((n, id) => { if (n.until <= this.time) this.nudges.delete(id); });
  }

  /** Seconds the renderer should hold the frame for (a kill of your own, the boss falling); cleared once read. */
  takeStop(): number {
    const s = this.stopWanted;
    this.stopWanted = 0;
    return s;
  }

  /** Handle every event of this frame (two passes so `ff` marks its `hit`). */
  onEvents(events: readonly GameEvent[], vm: ViewModel, localPid: number, deps: EffectDeps): void {
    this.ffTargets.clear();
    for (const ev of events) if (ev.e === 'ff') { this.ffTargets.add(ev.tgt); }
    for (const ev of events) this.onEvent(ev, vm, localPid, deps);
    for (const ev of events) if (ev.e === 'ff') deps.numbers.markFriendlyFire(ev.tgt);
  }

  private onEvent(ev: GameEvent, vm: ViewModel, localPid: number, deps: EffectDeps): void {
    const { particles, numbers, camera } = deps;
    const tmp = this.tmp;
    switch (ev.e) {
      case 'cast': this.onCast(ev, deps); return;
      case 'hit': {
        const h = FEEL.hit;
        this.flashes.set(ev.tgt, ev.crit
          ? { until: this.time + h.critFlashS, dur: h.critFlashS, peak: h.critFlashStrength }
          : { until: this.time + h.flashS, dur: h.flashS, peak: h.flashStrength });
        const found = findEntity(vm, ev.tgt, tmp);
        const z = found ? tmp.z : 0.5;
        // Shove the body away from the hit point (any direction when the hit is dead centre).
        let nx = found ? tmp.x - ev.x : 0, ny = found ? tmp.y - ev.y : 0;
        const nl = Math.hypot(nx, ny);
        if (nl < 0.05) {
          const a = Math.random() * Math.PI * 2;
          nx = Math.cos(a);
          ny = Math.sin(a);
        } else {
          nx /= nl;
          ny /= nl;
        }
        const push = ev.crit ? h.critNudgePx : h.nudgePx;
        this.nudges.set(ev.tgt, { dx: nx * push, dy: ny * push * 0.6, until: this.time + h.nudgeS });
        const mult: 0 | 2 | 3 = ev.crit ? (isFrozen(vm, ev.tgt) ? 3 : 2) : 0;
        numbers.hit(ev.tgt, ev.dmg, ev.type, ev.x, ev.y, z + 0.3, mult, this.ffTargets.has(ev.tgt));
        // an impact sprite where it lands, at most a few a second per target
        if (this.time - (this.hitShown.get(ev.tgt) ?? -1) >= HIT_SPRITE_GAP_S) {
          if (this.shots.spawn('fx-burst', `hit-${damageElement(ev.type)}`, ev.x, ev.y, z, 0, 1, Math.random() < 0.5)) this.hitShown.set(ev.tgt, this.time);
          if (this.hitShown.size > 256) this.hitShown.clear();
        }
        switch (ev.type) {
          case 'fire': particles.burst('fire', ev.x, ev.y, z, 3, 2); break;
          case 'cold': particles.burst('cold', ev.x, ev.y, z, 3, 2); break;
          case 'lightning': particles.sparks(ev.x, ev.y, z, 4); break;
          case 'water': case 'steam': particles.burst('water', ev.x, ev.y, z, 3, 2); break;
          case 'arcane': particles.burst('arcane', ev.x, ev.y, z, 3, 3); break;
          case 'life': particles.burst('life', ev.x, ev.y, z, 2, 1); break;
          case 'physical': particles.dust(ev.x, ev.y, 2, 1.5); break;
        }
        if (ev.tgt === localPid && ev.dmg >= 200) camera.shake(FEEL.shake.bigHit, 0.15);
        return;
      }
      case 'heal': {
        if (!findEntity(vm, ev.tgt, tmp)) return;
        numbers.heal(ev.tgt, ev.amt, tmp.x, tmp.y, tmp.z + 0.3);
        particles.burst('life', tmp.x, tmp.y, tmp.z, 6, 1.5);
        return;
      }
      case 'status': {
        if (!findEntity(vm, ev.tgt, tmp)) return;
        if (ev.on) {
          switch (ev.id) {
            case 'wet': particles.burst('water', tmp.x, tmp.y, tmp.z, 10, 3); break;
            case 'burning': particles.burst('fire', tmp.x, tmp.y, tmp.z, 10, 2); break;
            case 'chilled': particles.burst('cold', tmp.x, tmp.y, tmp.z, 8, 2); break;
            case 'frozen':
              particles.burst('ice', tmp.x, tmp.y, tmp.z, 14, 3);
              this.spawn('glyph', tmp.x, tmp.y, 0.6, 1, ELEMENT_SWATCH.ice.core, 'ice', null, 0, 0, tmp.z * 2 + 0.6);
              break;
            case 'shocked': particles.sparks(tmp.x, tmp.y, tmp.z, 8); break;
            case 'knockdown': particles.dust(tmp.x, tmp.y, 8, 2.5); break;
            case 'hasted': particles.streaks(13, tmp.x, tmp.y, 0.3, 0, 10, 4); break;
            case 'slowed': this.spawn('place', tmp.x, tmp.y, 0.5, 1.2, UI.muted); break;
          }
        } else if (ev.id === 'frozen') {
          particles.burst('ice', tmp.x, tmp.y, tmp.z, 16, 4); // thaw / shatter
          // the ice block cracks and falls apart where the body stands (tmp.z is about half its height)
          const size = frozenSize(tmp.z * 2 * UNIT);
          this.shots.spawn('fx-status', `frozen-${size}-thaw-fill`, tmp.x, tmp.y, 0, 0, ICE_FILL_ALPHA);
          this.shots.spawn('fx-status', `frozen-${size}-thaw`, tmp.x, tmp.y);
        }
        return;
      }
      case 'death': {
        if (!findEntity(vm, ev.tgt, tmp)) return;
        const enemy = vm.enemies.find((e) => e.id === ev.tgt);
        if (enemy) {
          particles.chunks(ENEMY_COLORS[enemy.k].body, tmp.x, tmp.y, tmp.z, enemy.boss ? 40 : 14, enemy.boss ? 6 : 4);
          particles.dust(tmp.x, tmp.y, enemy.boss ? 24 : 8, 2.5);
          if (enemy.boss) camera.shake(FEEL.shake.bossDeath, 0.5);
          // The body squashes flat while its chunks fly; a kill of your own holds the frame a beat.
          this.dying.push({ e: { ...enemy, st: [] }, t: 0, dur: (art.set(enemySetName(enemy)) ?? art.set(enemy.k))?.seconds('death') || FEEL.deathS });
          if (enemy.boss) this.stopWanted = Math.max(this.stopWanted, FEEL.hit.bossStopS);
          else if (ev.by === localPid) this.stopWanted = Math.max(this.stopWanted, FEEL.hit.stopS);
        } else {
          const player = vm.players.find((p) => p.id === ev.tgt);
          const col = player ? playerColor(player.color) : UI.hpRed;
          this.spawn('deathRing', tmp.x, tmp.y, 0.8, 1.5, col);
          particles.dust(tmp.x, tmp.y, 10, 2);
          if (ev.tgt === localPid) {
            camera.shake(FEEL.shake.ownDeath, 0.4);
            this.stopWanted = Math.max(this.stopWanted, FEEL.hit.ownDeathStopS);
            this.spawn('vignette', 0, 0, 1.2, 0, UI.hpRed);
          }
        }
        return;
      }
      case 'revive': {
        if (!findEntity(vm, ev.tgt, tmp)) return;
        this.spawn('revive', tmp.x, tmp.y, 0.9, 1.4, ELEMENT_SWATCH.life.core);
        particles.burst('life', tmp.x, tmp.y, 0.3, 24, 2);
        return;
      }
      case 'explosion': {
        const prim = ev.el[0] ?? 'fire';
        const sec = ev.el[1] ?? null;
        this.spawn('explosion', ev.x, ev.y, 0.5, ev.r, elementColor(prim), prim, sec, ev.kind === 'beamcross' ? 1 : 0);
        this.blastRing(ev.el.length > 0 ? ev.el : ['fire'], ev.x, ev.y, ev.r);
        const n = Math.min(60, 12 + Math.round(ev.r * 8));
        if (ev.el.length === 0) particles.burst('fire', ev.x, ev.y, 0.3, n, ev.r * 2);
        for (const e of ev.el) particles.burst(e, ev.x, ev.y, 0.3, Math.round(n / ev.el.length), ev.r * 2);
        particles.dust(ev.x, ev.y, 10, ev.r);
        camera.shake(ev.r * FEEL.shake.explosionPerUnit, 0.25);
        if (ev.kind === 'beamcross') {
          this.spawn('vignette', 0, 0, 0.5, 0, elementColor(prim), prim, sec);
          camera.shake(FEEL.shake.beamcross, 0.35);
        }
        if (ev.kind === 'meteor') {
          // A crater ring and rock chunks thrown up.
          this.spawn('quake', ev.x, ev.y, 0.9, ev.r, ELEMENT_SWATCH.lava.core, 'earth', 'fire');
          particles.chunks(ELEMENT_SWATCH.earth.dark, ev.x, ev.y, 0.2, 10, 5);
          particles.embers(ev.x, ev.y, 0.2, 8);
        }
        if (ev.kind === 'vortex') {
          this.spawn('nova', ev.x, ev.y, 0.6, ev.r * 1.6, ELEMENT_SWATCH.arcane.core, 'arcane');
          this.spawn('vignette', 0, 0, 0.35, 0, ELEMENT_SWATCH.arcane.core, 'arcane');
        }
        if (ev.kind === 'slam') {
          this.spawn('quake', ev.x, ev.y, 0.5, ev.r, elementColor(sec ?? 'earth'), 'earth', sec);
          particles.dust(ev.x, ev.y, 12, 3);
        }
        if (ev.kind === 'frost') {
          this.spawn('nova', ev.x, ev.y, 0.7, ev.r, ELEMENT_SWATCH.ice.core, 'ice', 'cold');
          particles.burst('ice', ev.x, ev.y, 0.3, 40, ev.r * 2.5);
          camera.shake(FEEL.shake.bossStomp * 1.5, 0.35);
        }
        return;
      }
      case 'boss': {
        if (ev.act === 'rage') {
          numbers.text(ev.x, ev.y, 3, 'ENRAGED', ELEMENT_SWATCH.ice.core, 22);
          this.spawn('nova', ev.x, ev.y, 0.8, 4.5, ELEMENT_SWATCH.ice.core, 'ice');
          this.spawn('vignette', 0, 0, 0.8, 0, ELEMENT_SWATCH.ice.core, 'ice');
          particles.burst('ice', ev.x, ev.y, 1, 50, 6);
          camera.shake(FEEL.shake.bossDeath, 0.7);
        }
        return;
      }
      case 'beam_merge':
        // two beams just crossed and bounced: a flash at the crossing that kicks the camera
        this.spawn('merge', ev.x, ev.y, 0.55, 1.8, UI.white, null, null, 0, 0, STAFF_TIP_H);
        particles.burst('life', ev.x, ev.y, STAFF_TIP_H, 12, 4);
        particles.burst('arcane', ev.x, ev.y, STAFF_TIP_H, 12, 4);
        particles.sparks(ev.x, ev.y, STAFF_TIP_H, 16);
        camera.shake(FEEL.shake.beamcross * 0.4, 0.2);
        return;
      case 'shock_self': {
        if (!findEntity(vm, ev.p, tmp)) return;
        this.spawn('shock', tmp.x, tmp.y, 0.35, 0.9, ELEMENT_SWATCH.lightning.core);
        particles.sparks(tmp.x, tmp.y, 0.5, 24);
        numbers.text(tmp.x, tmp.y, 1.6, 'ZAP!', ELEMENT_SWATCH.lightning.core, 16);
        if (ev.p === localPid) camera.shake(FEEL.shake.ownShock, 0.3);
        return;
      }
      case 'magick': this.onMagick(ev, deps); return;
      // Deeper spells: a finished weave flashes the new piece's colours; Time Stop lands with a gold ring and a shake.
      case 'woven':
        particles.burst(ev.el, ev.x, ev.y, 0.6, 10 + 6 * ev.tier, 2 + ev.tier);
        return;
      case 'grand': {
        // each Grand Magick bursts in its own pieces' colours
        const [a, b] = GRAND_COLOURS[ev.id];
        if (ev.o === 'cast') {
          particles.burst(a, ev.x, ev.y, 0.6, 50, 7);
          particles.burst(b, ev.x, ev.y, 0.4, 30, 4);
          camera.shake(FEEL.shake.conflagration, 0.35);
        } else if (ev.o === 'start') {
          particles.burst(a, ev.x, ev.y, 0.5, 14, 2);
        }
        return;
      }
      case 'ward':
        particles.burst('pulse', ev.x, ev.y, 0.6, 24, 4);
        numbers.text(ev.x, ev.y, 1.6, 'SAVED!', ELEMENT_SWATCH.pulse.core, 16);
        return;
      case 'doom':
        particles.burst('death', ev.x, ev.y, 0.7, 26, 4);
        if (ev.tgt === localPid) camera.shake(FEEL.shake.ownShock, 0.3);
        return;
      case 'bolt': {
        this.spawn('bolt', ev.x, ev.y, 0.4, 1, ELEMENT_SWATCH.lightning.core);
        particles.sparks(ev.x, ev.y, 0.5, 30);
        particles.burst('lightning', ev.x, ev.y, 0.5, 20, 6);
        camera.shake(FEEL.shake.bolt, 0.35);
        return;
      }
      case 'queue_full': case 'cancel': case 'wave': case 'ff': return;
      // story terrain: a body going over the edge, lava filling and setting, brambles catching
      case 'fall':
        particles.dust(ev.x, ev.y, 14, 3);
        return;
      case 'lava':
        if (ev.act === 'fill') particles.burst('fire', ev.x, ev.y, 0.2, 18, 3);
        else if (ev.act === 'set') { particles.burst('cold', ev.x, ev.y, 0.2, 14, 2); particles.dust(ev.x, ev.y, 10, 1.5); }
        return;
      case 'bramble':
        if (ev.act === 'burn') particles.burst('fire', ev.x, ev.y, 0.4, 4, 1.5);
        else particles.dust(ev.x, ev.y, 4, 1);
        return;
    }
  }

  private onCast(ev: Extract<GameEvent, { e: 'cast' }>, deps: EffectDeps): void {
    const { particles, camera } = deps;
    const dom = dominantOf(ev.el);
    const col = dom ? elementColor(dom) : UI.white;
    switch (ev.kind) {
      case 'beam': case 'spray': case 'arc':
        this.spawn('muzzle', ev.x, ev.y, 0.15, 0.5, col, dom, null, 0, ev.ang, STAFF_TIP_H);
        return;
      case 'projectile':
        particles.burst(dom === 'ice' ? 'ice' : 'earth', ev.x + Math.cos(ev.ang) * 0.6, ev.y + Math.sin(ev.ang) * 0.6, STAFF_TIP_H, 6, 2);
        return;
      case 'nova': {
        const r = NOVA.baseRadius + NOVA.radiusPerExtraElement * Math.max(0, ev.el.length - 1);
        const sec = ev.el.find((e) => e !== dom) ?? null;
        this.spawn('nova', ev.x, ev.y, 0.45, r, col, dom, sec);
        this.novaFront(ev.el.length > 0 ? ev.el : ['arcane'], ev.x, ev.y, r);
        for (const e of ev.el) particles.burst(e, ev.x, ev.y, 0.3, Math.round(40 / ev.el.length), r * 2.5);
        camera.shake(FEEL.shake.nova, 0.2);
        return;
      }
      case 'quake': {
        const nEarth = ev.el.filter((e) => e === 'earth').length;
        this.spawn('quake', ev.x, ev.y, 1.5, QUAKE.baseRadius + nEarth, ELEMENT_SWATCH.earth.core, 'earth', ev.el.find((e) => e !== 'earth') ?? null);
        this.novaFront(['earth'], ev.x, ev.y, QUAKE.baseRadius + nEarth);
        particles.dust(ev.x, ev.y, 30, 4);
        particles.chunks(ELEMENT_SWATCH.earth.dark, ev.x, ev.y, 0.1, 12, 4);
        camera.shake(FEEL.shake.quake, 0.25);
        return;
      }
      case 'barrier': this.spawn('place', ev.x, ev.y, 0.35, 2.5, ELEMENT_SWATCH.shield.core, 'shield'); return;
      case 'wall': {
        const solid = ev.el.includes('ice') ? 'ice' : 'earth';
        this.spawn('place', ev.x, ev.y, 0.35, 2.5, elementColor(solid), solid);
        particles.dust(ev.x, ev.y, 16, 3);
        particles.burst(solid, ev.x, ev.y, 0.2, 16, 3);
        camera.shake(FEEL.shake.wall, 0.15);
        return;
      }
      case 'mines': case 'storm': case 'aura': {
        const payload = ev.el.find((e) => e !== 'shield') ?? 'shield';
        this.spawn('place', ev.x, ev.y, 0.35, ev.kind === 'aura' ? 3 : 2.5, elementColor(payload), payload);
        particles.burst(payload, ev.x, ev.y, 0.3, 12, 3);
        return;
      }
      case 'self': {
        this.spawn('selfRing', ev.x, ev.y, 0.3, 1.2, col, dom);
        if (dom) this.spawn('glyph', ev.x, ev.y, 0.5, 1, col, dom, null, 0, 0, 1.6);
        for (const e of ev.el) particles.burst(e, ev.x, ev.y, 0.5, 8, 2);
        return;
      }
      case 'bubble': this.spawn('place', ev.x, ev.y, 0.3, 0.8, ELEMENT_SWATCH.shield.core, 'shield'); return;
      case 'armor': {
        const solid = ev.el.includes('ice') ? 'ice' : 'earth';
        particles.burst(solid, ev.x, ev.y, 0.5, 14, 2);
        this.spawn('selfRing', ev.x, ev.y, 0.3, 1.0, elementColor(solid), solid);
        return;
      }
      case 'melee': {
        // Shape from the imbue (DESIGN 2.4 Weapon column): earth fissure, ice slash, life/arcane swipe, fluids thrust, empty swing.
        const shape = dom === 'earth' ? 2 : dom === 'ice' ? 3 : dom === 'life' || dom === 'arcane' || dom === null ? 0 : 1;
        this.spawn('wedge', ev.x, ev.y, shape === 2 ? 0.8 : 0.18, 1, dom ? col : UI.white, dom, null, shape, ev.ang);
        if (shape === 2) { particles.dust(ev.x + Math.cos(ev.ang) * 2, ev.y + Math.sin(ev.ang) * 2, 10, 2); camera.shake(FEEL.shake.fissure, 0.2); }
        return;
      }
      case 'wind':
        this.spawn('wind', ev.x, ev.y, 0.18, 3, UI.muted, null, null, ev.m === 'area' ? 1 : 0, ev.ang);
        particles.streaks(11, ev.x, ev.y, 0.4, ev.ang, ev.m === 'area' ? 24 : 12, 9);
        return;
      case 'none': return;
    }
  }

  private onMagick(ev: Extract<GameEvent, { e: 'magick' }>, deps: EffectDeps): void {
    const { particles, numbers, camera } = deps;
    numbers.text(ev.x, ev.y, 1.8, MAGICKS[ev.id].name, UI.gold, 16);
    this.spawn('magick', ev.x, ev.y, 0.7, 2.5, UI.gold);
    switch (ev.id) {
      case 'rain': particles.burst('water', ev.x, ev.y, 1.5, 40, 6); break;
      case 'blizzard': particles.burst('cold', ev.x, ev.y, 1.5, 40, 6); break;
      case 'conflagration': particles.burst('fire', ev.x, ev.y, 0.3, 60, 10); this.spawn('nova', ev.x, ev.y, 0.5, 8, ELEMENT_SWATCH.fire.core, 'fire', 'steam'); camera.shake(FEEL.shake.conflagration, 0.3); break;
      case 'phoenix': particles.burst('fire', ev.x, ev.y, 0.3, 40, 6); particles.burst('life', ev.x, ev.y, 0.5, 24, 3); this.spawn('nova', ev.x, ev.y, 0.5, 4, ELEMENT_SWATCH.fire.core, 'fire', 'life'); camera.shake(FEEL.shake.phoenix, 0.3); break;
      case 'haste': particles.streaks(13, ev.x, ev.y, 0.4, 0, 20, 6); break;
      case 'teleport': particles.burst('arcane', ev.x, ev.y, 0.5, 20, 4); particles.burst('lightning', ev.x, ev.y, 0.5, 10, 4); break;
      case 'nullify': particles.burst('shield', ev.x, ev.y, 0.5, 24, 5); break;
      case 'timewarp': particles.burst('cold', ev.x, ev.y, 0.5, 16, 3); break;
      case 'revive': particles.burst('life', ev.x, ev.y, 0.5, 16, 3); break;
      case 'thunderbolt': break; // the `bolt` event draws the strike itself
      // The five placed Magicks draw themselves as markers; the cast gets a puff of their elements.
      case 'meteor': particles.burst('lava', ev.x, ev.y, 1.2, 20, 3); particles.embers(ev.x, ev.y, 1, 10); break;
      case 'tornado': particles.burst('steam', ev.x, ev.y, 0.5, 24, 5); particles.streaks(PC.white, ev.x, ev.y, 0.4, 0, 14, 7); break;
      case 'thunderstorm': particles.burst('steam', ev.x, ev.y, 1.5, 20, 3); particles.sparks(ev.x, ev.y, 1.2, 10); break;
      case 'vortex': particles.burst('arcane', ev.x, ev.y, 0.5, 24, 4); break;
      case 'elemental': particles.dust(ev.x, ev.y, 16, 3); particles.chunks(ELEMENT_SWATCH.earth.core, ev.x, ev.y, 0.2, 8, 3); break;
    }
  }

  /**
   * An explosion from the effects library: one blast at the centre, and for a big one a ring of blasts at 0.6 of
   * its radius going off a moment later, in its elements in turn. The sprites stay at their pixel size; a bigger
   * explosion is more of them.
   */
  private blastRing(el: readonly Element[], x: number, y: number, r: number): void {
    this.shots.spawn('fx-burst', `blast-${fxElement(el[0]!)}`, x, y);
    if (r < 1.6) return;
    const n = Math.min(10, Math.round(r * 3));
    for (let k = 0; k < n; k++) {
      const a = (Math.PI * 2 * k) / n + Math.random() * 0.4;
      this.shots.spawn('fx-burst', `blast-${fxElement(el[k % el.length]!)}`, x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6, 0, 0.05 + Math.random() * 0.06, 1, k % 2 === 1);
    }
  }

  /** A nova's front: blasts round the caster at half the radius, then at nine tenths of it a beat later. */
  private novaFront(el: readonly Element[], x: number, y: number, r: number): void {
    const inner = 6, outer = Math.min(16, Math.round(r * 3.4));
    for (let k = 0; k < inner; k++) {
      const a = (Math.PI * 2 * k) / inner;
      this.shots.spawn('fx-burst', `blast-${fxElement(el[k % el.length]!)}`, x + Math.cos(a) * r * 0.45, y + Math.sin(a) * r * 0.45, 0, 0, 1, k % 2 === 0);
    }
    for (let k = 0; k < outer; k++) {
      const a = (Math.PI * 2 * (k + 0.5)) / outer;
      this.shots.spawn('fx-burst', `blast-${fxElement(el[(k + 1) % el.length]!)}`, x + Math.cos(a) * r * 0.9, y + Math.sin(a) * r * 0.9, 0, 0.08, 1, k % 2 === 1);
    }
  }

  /**
   * A shield or wall that vanished breaks along its line (shards, rubble, ice); one that had worn down to nothing
   * just fades in fewer, fainter shards. A mine that went off flares on its plate under the blast.
   */
  onHazardGone(h: HazardSeen): void {
    if (h.k === 'mine') {
      const payload = h.el.find((e) => e !== 'shield') ?? 'arcane';
      this.shots.spawn('fx-mine', `trigger-${fxElement(payload)}`, h.rx, h.ry);
      return;
    }
    if (h.k !== 'barrier' && h.k !== 'wall') return;
    const set = wallSet(h);
    const clip = set === 'fx-rock-wall' ? 'crumble' : 'shatter';
    const worn = wallHpFrac(h) < 0.08;
    const segs = hazardSegments(h, this.time);
    const every = segs.length > 12 ? 2 : 1;
    for (let j = 0; j < segs.length; j += every) {
      const [a, b] = segs[j]!;
      this.shots.spawn(set, clip, (a.x + b.x) / 2, (a.y + b.y) / 2, 0, Math.random() * 0.08, worn ? 0.6 : 1, j % 2 === 1);
    }
  }

  step(dt: number): void {
    this.time += dt;
    this.shots.step(dt);
    for (let i = this.dying.length - 1; i >= 0; i--) {
      const d = this.dying[i]!;
      d.t += dt;
      if (d.t >= d.dur) this.dying.splice(i, 1);
    }
    for (const e of this.pool) {
      if (!e.active) continue;
      e.t += dt;
      if (e.t >= e.dur) e.active = false;
    }
  }

  /** Floor-level effects (rings, cracks, wedges). */
  drawGround(ctx: Ctx, cam: Camera, time: number): void { drawEffectsGround(ctx, cam, time, this.pool); }
  /** Above-ground effects (flashes, bolts, glyph pops, muzzle flashes). */
  drawAir(ctx: Ctx, cam: Camera, time: number): void {
    this.shots.draw(ctx, cam);
    drawEffectsAir(ctx, cam, time, this.pool);
  }
  /** Screen-space vignettes (beam cross, own death). */
  drawScreen(ctx: Ctx, w: number, h: number): void { drawEffectsScreen(ctx, w, h, this.pool); }
}
